import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { Bell, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";
import { toast } from "sonner";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data || []);
    } catch (err) {
      console.debug("notifications fetch failed", err?.response?.status);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const unread = items.filter((i) => !i.read).length;

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((i) => ({ ...i, read: true })));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markOne = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, read: true } : i)));
    } catch (err) { console.error("markOne failed", err); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative p-2 rounded-sm hover:bg-white/10 transition-colors"
          aria-label="Notifications"
        >
          <Bell size={18} className="text-white" />
          {unread > 0 && (
            <span
              data-testid="notification-unread-count"
              className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-[#e65f2b] text-black text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 bg-[#0f0f0f] border-white/10 text-white p-0">
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="font-display uppercase tracking-widest text-sm">Notifications</div>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs uppercase tracking-widest hover:bg-white/10"
              onClick={markAll}
              data-testid="notif-mark-all-read"
            >
              <Check size={12} className="mr-1" />Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <div className="p-6 text-sm text-white/50 text-center">No notifications yet.</div>}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => markOne(n.id)}
              data-testid={`notif-item-${n.id}`}
              className={`w-full text-left p-4 border-b border-white/5 hover:bg-white/[0.03] transition-colors ${
                !n.read ? "bg-[#e65f2b]/5" : ""
              }`}
            >
              <div className="flex items-start gap-3">
                {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#e65f2b] shrink-0" />}
                <div className="flex-1">
                  <div className="font-bold text-sm">{n.title}</div>
                  <div className="text-xs text-white/70 mt-1">{n.message}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-2">
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
